import React from "react";

// Sample A — "Hearth & Home"
// Warm, cozy, family-friendly. Earthy palette (cream, terracotta, sage, ink),
// soft rounded cards, generous spacing, serif display font for warmth.

const SampleA = () => {
  return (
    <div
      data-testid="sample-a-root"
      className="min-h-screen"
      style={{
        background: "#F6EFE4",
        color: "#2A1F17",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,800&family=Inter:wght@400;500;600;700&display=swap');
        .sa-display { font-family: 'Fraunces', serif; letter-spacing: -0.02em; }
        .sa-card {
          background: #FFFBF3;
          border-radius: 28px;
          border: 1px solid rgba(42,31,23,0.08);
          box-shadow: 0 1px 0 rgba(42,31,23,0.04), 0 24px 48px -24px rgba(42,31,23,0.18);
          transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s;
        }
        .sa-card:hover { transform: translateY(-4px); box-shadow: 0 1px 0 rgba(42,31,23,0.04), 0 32px 64px -28px rgba(42,31,23,0.25); }
        .sa-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 16px; border-radius: 999px;
          background: #2A1F17; color: #F6EFE4;
          font-size: 13px; font-weight: 600;
          transition: background .25s, transform .25s;
        }
        .sa-pill:hover { background: #C75D3F; transform: translateY(-1px); }
        .sa-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px; border-radius: 999px;
          font-size: 12px; font-weight: 600;
        }
        .sa-grain {
          background-image: radial-gradient(rgba(42,31,23,.05) 1px, transparent 1px);
          background-size: 4px 4px;
        }
        @keyframes saFloat { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
        .sa-float { animation: saFloat 6s ease-in-out infinite; }
      `}</style>

      {/* Top bar */}
      <header className="px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: "#C75D3F", color: "#FFFBF3" }}
          >
            <span className="sa-display text-xl font-extrabold">f</span>
          </div>
          <div>
            <div className="sa-display text-2xl font-extrabold leading-none">familytable</div>
            <div className="text-xs opacity-60 mt-1">The Anderson Family</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium opacity-80">
          <span>Home</span><span>Calendar</span><span>Recipes</span><span>Journal</span><span>Tracker</span>
        </nav>
        <div className="flex items-center gap-3">
          <button className="sa-pill">+ Quick add</button>
          <div className="w-9 h-9 rounded-full" style={{ background: "#7E9F6B" }} />
        </div>
      </header>

      <main className="px-10 pb-20 max-w-7xl mx-auto">
        {/* Hero */}
        <section className="grid md:grid-cols-3 gap-8 items-end mb-10">
          <div className="md:col-span-2">
            <div className="sa-chip mb-5" style={{ background: "#EFE3CF", color: "#2A1F17" }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C75D3F" }} /> Sunday, gentle morning
            </div>
            <h1 className="sa-display text-6xl md:text-7xl font-extrabold leading-[0.95]">
              Good morning,<br />
              <span style={{ color: "#C75D3F" }}>Maya</span>.
            </h1>
            <p className="mt-5 text-base opacity-70 max-w-lg">
              Three little things on the family's mind today — and Sunday roast at 6.
              Toby has soccer in two hours.
            </p>
          </div>

          <div className="sa-card p-6 sa-float">
            <div className="flex items-center justify-between mb-4">
              <span className="sa-display text-sm font-semibold opacity-70">Today's weather</span>
              <span className="text-xs opacity-50">Brooklyn, NY</span>
            </div>
            <div className="flex items-end gap-4">
              <div className="sa-display text-6xl font-extrabold">62°</div>
              <div className="pb-2">
                <div className="text-sm font-semibold">Light rain</div>
                <div className="text-xs opacity-60">High 68 · Low 54</div>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-black/5 text-xs opacity-70">
              Bring umbrellas — Toby's soccer might be muddy.
            </div>
          </div>
        </section>

        {/* Tile grid */}
        <section className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="sa-card p-7">
            <div className="flex items-center justify-between mb-5">
              <span className="sa-display text-xl font-bold">Today's plan</span>
              <span className="sa-chip" style={{ background: "#EFE3CF" }}>4 items</span>
            </div>
            <ul className="space-y-4">
              {[
                ["8:30", "School drop-off", "#7E9F6B"],
                ["10:00", "Soccer · Toby", "#C75D3F"],
                ["14:00", "Dentist · Mom", "#5B7E9C"],
                ["18:00", "Sunday roast", "#D4A24C"],
              ].map(([t, label, c], i) => (
                <li key={i} className="flex items-center gap-4">
                  <span style={{ background: c }} className="w-2 h-12 rounded-full" />
                  <div>
                    <div className="font-semibold">{label}</div>
                    <div className="text-xs opacity-60">{t}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="sa-card p-7" style={{ background: "#2A1F17", color: "#F6EFE4" }}>
            <div className="flex items-center justify-between mb-5">
              <span className="sa-display text-xl font-bold">Tonight's recipe</span>
              <span className="sa-chip" style={{ background: "#C75D3F", color: "#FFFBF3" }}>40 min</span>
            </div>
            <div className="sa-display text-3xl font-extrabold leading-tight mb-3">
              Slow-roasted herb chicken
            </div>
            <p className="text-sm opacity-70 mb-5">
              With rosemary potatoes, carrots, and lemon. Family-favorite from Grandma Eve.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Rosemary", "Lemon", "Potatoes", "Garlic"].map((t) => (
                <span key={t} className="sa-chip" style={{ background: "rgba(255,251,243,0.1)", color: "#F6EFE4" }}>{t}</span>
              ))}
            </div>
          </div>

          <div className="sa-card p-7">
            <div className="flex items-center justify-between mb-5">
              <span className="sa-display text-xl font-bold">Family circle</span>
              <span className="text-xs opacity-50">live</span>
            </div>
            <div className="space-y-4">
              {[
                ["Maya", "At home", "#7E9F6B"],
                ["David", "Coffee shop · 0.4 mi", "#D4A24C"],
                ["Toby", "School", "#C75D3F"],
                ["Lily", "Grandma's", "#5B7E9C"],
              ].map(([name, where, c], i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold"
                       style={{ background: c, color: "#FFFBF3" }}>{name[0]}</div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{name}</div>
                    <div className="text-xs opacity-60">{where}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Wide tiles */}
        <section className="grid md:grid-cols-2 gap-6">
          <div className="sa-card p-7">
            <div className="flex items-center justify-between mb-4">
              <span className="sa-display text-xl font-bold">Sunday journal</span>
              <span className="text-xs opacity-50">3 new entries</span>
            </div>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-16 h-16 rounded-xl sa-grain" style={{ background: "#EFE3CF" }} />
                <div>
                  <div className="text-sm font-semibold">Lily's first lost tooth</div>
                  <div className="text-xs opacity-60">Maya · 2 hours ago</div>
                  <p className="text-sm mt-1 opacity-80">She put it under the pillow with a tiny note.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-16 h-16 rounded-xl" style={{ background: "#7E9F6B" }} />
                <div>
                  <div className="text-sm font-semibold">Garden first sprouts!</div>
                  <div className="text-xs opacity-60">David · yesterday</div>
                  <p className="text-sm mt-1 opacity-80">Tomatoes are coming up. Basil hasn't woken yet.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="sa-card p-7" style={{ background: "#7E9F6B", color: "#FFFBF3" }}>
            <span className="sa-chip mb-5" style={{ background: "rgba(255,255,255,0.18)", color: "#FFFBF3" }}>
              AI weekly briefing
            </span>
            <div className="sa-display text-3xl font-extrabold leading-tight mb-3">
              Calmer week ahead — no overlapping appointments.
            </div>
            <p className="text-sm opacity-90 mb-5">
              Tuesday looks busy with Toby's tutor + Lily's piano. You have one open evening
              on Thursday — date night?
            </p>
            <button className="sa-pill" style={{ background: "#FFFBF3", color: "#2A1F17" }}>
              Read full briefing →
            </button>
          </div>
        </section>
      </main>

      <footer className="px-10 pb-10 text-center text-xs opacity-40">
        Sample A · Hearth &amp; Home — warm, cozy, family-first
      </footer>
    </div>
  );
};

export default SampleA;
